import { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import { apiFetch } from '../utils/api';
import { resolveTotalVentasReales } from '../utils/ventasReales';
import { useAuth } from '../context/AuthContext';
import DenominacionEfectivo from '../components/DenominacionEfectivo';
import {
    createEmptyDesglose,
    compactDesglose,
    parseMontoClp,
    validateDesgloseVsMonto,
    formatDesgloseDetalle,
    desgloseLineas,
} from '../utils/denominacionesClp';
import { formatFechaHoraChile, TZ_CHILE } from '../utils/fechasChile';
import { printComprobanteMovimientoEfectivo } from '../utils/printMovimientoEfectivo';
import { useTableSort } from '../hooks/useTableSort';
import SortableTh from '../components/SortableTh';
import {
    BarChart3,
    TrendingUp,
    Package,
    Calendar,
    DollarSign,
    ShoppingCart,
    Loader2,
    AlertCircle,
    Trophy,
    Users,
    Download,
    Clock,
    AlertTriangle,
    Eye,
    Printer,
    Lock,
} from 'lucide-react';

const ROLE_ADMIN = 4;

const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
    }).format(amount || 0);

const formatDateLong = (s) => {
    if (!s) return '—';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('es-CL', {
        weekday: 'long',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: TZ_CHILE,
    });
};

const formatDateShort = (s) => {
    if (!s) return '—';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: '2-digit', timeZone: TZ_CHILE });
};

const formatTime = (s) => {
    if (!s) return '—';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-CL', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: TZ_CHILE,
    });
};

/** Renderiza ISO 8601 con offset (`2026-05-19T01:45:10-04:00`) tal como viene
 * del backend, sin reformatear, para conservar la precisión exacta. */
const renderISO = (iso) => {
    if (!iso) return '—';
    return iso;
};

const isoDay = (offsetDays) => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    return d.toISOString().slice(0, 10);
};

const downloadCSV = (filename, rows) => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const escape = (v) => {
        if (v === null || v === undefined) return '';
        const s = String(v).replace(/"/g, '""');
        return /[",\n;]/.test(s) ? `"${s}"` : s;
    };
    const csv =
        headers.join(';') +
        '\n' +
        rows.map((r) => headers.map((h) => escape(r[h])).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

export default function ReportsPage() {
    const { user } = useAuth();
    const isAdmin = Number(user?.role) === ROLE_ADMIN;

    const [fechaDesde, setFechaDesde] = useState(isoDay(-30));
    const [fechaHasta, setFechaHasta] = useState(isoDay(0));

    const [topProducts, setTopProducts] = useState([]);
    const [preventasByVendedor, setPreventasByVendedor] = useState([]);
    const [diarios, setDiarios] = useState({ rows: [], fecha_desde: '', fecha_hasta: '' });
    const [porCaja, setPorCaja] = useState({ rows: [], fecha_desde: '', fecha_hasta: '' });
    const [cierres, setCierres] = useState({ rows: [], fecha_desde: '', fecha_hasta: '' });

    const [selectedCierreId, setSelectedCierreId] = useState(null);
    const [cierreDetail, setCierreDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [errorDetail, setErrorDetail] = useState(null);

    const [closeTurnoTarget, setCloseTurnoTarget] = useState(null); // { apertura_id, caja_nombre, usuario_nombre }
    const [closeTurnoResumen, setCloseTurnoResumen] = useState(null);
    const [closeTurnoEfectivo, setCloseTurnoEfectivo] = useState('');
    const [closeTurnoDesglose, setCloseTurnoDesglose] = useState(() => createEmptyDesglose());
    const [closeTurnoForzar, setCloseTurnoForzar] = useState(false);
    const [closeTurnoLoading, setCloseTurnoLoading] = useState(false);
    const [closeTurnoError, setCloseTurnoError] = useState(null);

    const [loading, setLoading] = useState(true);
    const [reloading, setReloading] = useState(false);
    const [error, setError] = useState(null);

    const handleVerDetalle = async (aperturaId) => {
        setSelectedCierreId(aperturaId);
        setLoadingDetail(true);
        setErrorDetail(null);
        setCierreDetail(null);
        try {
            const res = await apiFetch(`${API_URL}/api/cajas/cierre-detalle/${aperturaId}`);
            if (res.ok) {
                const data = await res.json();
                setCierreDetail(data);
            } else {
                const errData = await res.json();
                setErrorDetail(errData.error || 'No se pudo cargar el detalle del cierre.');
            }
        } catch (err) {
            console.error(err);
            setErrorDetail('Error de red al cargar el detalle del cierre.');
        } finally {
            setLoadingDetail(false);
        }
    };

    const openCloseTurnoModal = async (row) => {
        const aperturaId = row.apertura_id;
        setCloseTurnoTarget({
            apertura_id: aperturaId,
            caja_nombre: row.caja_nombre || cierreDetail?.caja_nombre || '—',
            usuario_nombre: row.usuario_nombre || cierreDetail?.usuario_nombre || '—',
        });
        setCloseTurnoResumen(null);
        setCloseTurnoEfectivo('');
        setCloseTurnoDesglose(createEmptyDesglose());
        setCloseTurnoForzar(false);
        setCloseTurnoError(null);
        setCloseTurnoLoading(true);
        try {
            const res = await apiFetch(`${API_URL}/api/cajas/cierre-resumen?apertura_id=${aperturaId}`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setCloseTurnoError(data.error || 'No se pudo cargar el resumen de cierre.');
                return;
            }
            setCloseTurnoResumen(data);
            const esperado = data.efectivo_esperado_en_caja;
            if (esperado != null && Number.isFinite(Number(esperado))) {
                setCloseTurnoEfectivo(String(Math.round(Number(esperado))));
            }
        } catch (err) {
            console.error(err);
            setCloseTurnoError('Error de red al cargar el resumen de cierre.');
        } finally {
            setCloseTurnoLoading(false);
        }
    };

    const submitCloseTurno = async () => {
        if (!closeTurnoTarget?.apertura_id) return;
        const efectivo = parseMontoClp(closeTurnoEfectivo);
        if (efectivo === null) {
            setCloseTurnoError('Indique el efectivo contado en caja.');
            return;
        }
        const mismatch = validateDesgloseVsMonto(closeTurnoDesglose, efectivo);
        if (mismatch) {
            setCloseTurnoError(mismatch);
            return;
        }
        const esperado = closeTurnoResumen?.efectivo_esperado_en_caja;
        const hayDescuadre =
            esperado != null && Number.isFinite(Number(esperado)) && Math.abs(efectivo - Number(esperado)) > 1;
        if (hayDescuadre && !closeTurnoForzar) {
            setCloseTurnoError(
                `El efectivo contado (${formatCurrency(efectivo)}) no coincide con el esperado (${formatCurrency(esperado)}). Marque “Cerrar con descuadre” para continuar.`
            );
            return;
        }
        setCloseTurnoLoading(true);
        setCloseTurnoError(null);
        try {
            const res = await apiFetch(`${API_URL}/api/cajas/close`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    apertura_id: closeTurnoTarget.apertura_id,
                    efectivo_cierre: efectivo,
                    desglose: compactDesglose(closeTurnoDesglose),
                    ...(hayDescuadre && closeTurnoForzar ? { forzar_cierre: true } : {}),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const msg =
                    data.detalle?.efectivo != null
                        ? `${data.error || 'No se pudo cerrar'}\nEsperado: ${formatCurrency(data.detalle.efectivo.esperado)}\nContado: ${formatCurrency(data.detalle.efectivo.ingresado)}`
                        : data.error || 'No se pudo cerrar la caja';
                setCloseTurnoError(msg);
                if (data.puede_forzar_cierre) setCloseTurnoForzar(false);
                return;
            }
            setCloseTurnoTarget(null);
            setCloseTurnoResumen(null);
            setCloseTurnoEfectivo('');
            setCloseTurnoDesglose(createEmptyDesglose());
            setSelectedCierreId(null);
            setCierreDetail(null);
            await fetchReports();
            if (data.corte_turno) {
                setCierreDetail(data.corte_turno);
                setSelectedCierreId(data.corte_turno.apertura_id || closeTurnoTarget.apertura_id);
            }
        } catch (err) {
            console.error(err);
            setCloseTurnoError('Error de red al cerrar la caja.');
        } finally {
            setCloseTurnoLoading(false);
        }
    };

    const fetchReports = async (opts = {}) => {
        const desde = opts.desde ?? fechaDesde;
        const hasta = opts.hasta ?? fechaHasta;
        const setter = opts.firstLoad ? setLoading : setReloading;
        setter(true);
        setError(null);
        try {
            const qs = `?fecha_desde=${desde}&fecha_hasta=${hasta}`;
            const [diariosRes, cajaRes, cierresRes, productsRes, vendedoresRes] = await Promise.all([
                apiFetch(`${API_URL}/api/reports/totales-diarios${qs}`),
                apiFetch(`${API_URL}/api/reports/totales-dia-operacional${qs}`),
                apiFetch(`${API_URL}/api/reports/cierres-turno${qs}`),
                apiFetch(`${API_URL}/api/reports/top-products`),
                apiFetch(`${API_URL}/api/reports/preventas-by-vendedor`),
            ]);
            if (diariosRes.ok) setDiarios(await diariosRes.json());
            if (cajaRes.ok) setPorCaja(await cajaRes.json());
            if (cierresRes.ok) setCierres(await cierresRes.json());
            if (productsRes.ok) setTopProducts(await productsRes.json());
            if (vendedoresRes.ok) setPreventasByVendedor(await vendedoresRes.json());
        } catch (err) {
            console.error(err);
            setError(err.message || 'Error al cargar reportes');
        } finally {
            setter(false);
        }
    };

    useEffect(() => {
        fetchReports({ firstLoad: true });
    }, []);

    const totals = useMemo(() => {
        const r = diarios.rows || [];
        const totalBruto = r.reduce((s, x) => s + (x.total_bruto || 0), 0);
        const totalVentas = r.reduce((s, x) => s + (x.cantidad_ventas || 0), 0);
        const totalEfectivo = r.reduce((s, x) => s + (x.efectivo || 0), 0);
        const totalTarjeta = r.reduce((s, x) => s + (x.tarjeta || 0), 0);
        const totalTransfer = r.reduce((s, x) => s + (x.transferencia || 0), 0);
        const dias = r.length;
        const promedioDiario = dias > 0 ? totalBruto / dias : 0;
        return { totalBruto, totalVentas, totalEfectivo, totalTarjeta, totalTransfer, dias, promedioDiario };
    }, [diarios]);

    const cierresWarnings = useMemo(
        () =>
            (cierres.rows || []).filter((c) => {
                if (!c.opened_at || !c.closed_at) return false;
                const horas =
                    (new Date(c.closed_at) - new Date(c.opened_at)) / (1000 * 60 * 60);
                return horas > 24;
            }),
        [cierres]
    );

    const diariosSort = useTableSort(diarios.rows, {
        defaultKey: 'fecha_operacional',
        defaultDir: 'desc',
        getters: {
            cantidad_ventas: (r) => Number(r.cantidad_ventas) || 0,
            efectivo: (r) => Number(r.efectivo) || 0,
            tarjeta: (r) => Number(r.tarjeta) || 0,
            transferencia: (r) => Number(r.transferencia) || 0,
            total_bruto: (r) => Number(r.total_bruto) || 0,
            cajas_activas: (r) => Number(r.cajas_activas) || 0,
        },
    });

    const cierresSort = useTableSort(cierres.rows, {
        defaultKey: 'fecha_operacional',
        defaultDir: 'desc',
        getters: {
            tiempo: (c) => Number(c.elapsed_seconds) || 0,
            cantidad_ventas: (c) => Number(c.cantidad_ventas) || 0,
            monto_cierre_efectivo: (c) => Number(c.monto_cierre_efectivo) || 0,
            total_ventas: (c) => Number(c.total_ventas) || 0,
            caja: (c) => `${c.caja_nombre || ''} ${c.usuario_nombre || ''}`,
            apertura: (c) => c.opened_at_iso || c.opened_at || c.fecha_apertura,
            cierre: (c) => c.closed_at_iso || c.closed_at || c.fecha_cierre || '',
            estado: (c) => (c.estado ? 1 : 0),
            sesion: (c) => c.session_id || String(c.apertura_id || ''),
        },
    });

    const topProductsSort = useTableSort(topProducts, {
        defaultKey: 'revenue',
        defaultDir: 'desc',
        getters: {
            quantity_sold: (p) => Number(p.quantity_sold) || 0,
            revenue: (p) => Number(p.revenue) || 0,
        },
    });

    const vendedoresSort = useTableSort(preventasByVendedor, {
        defaultKey: 'total_monto',
        defaultDir: 'desc',
        getters: {
            total_preventas: (v) => Number(v.total_preventas) || 0,
            total_monto: (v) => Number(v.total_monto) || 0,
        },
    });

    const pagosSort = useTableSort(cierreDetail?.ventas_por_forma_pago || [], {
        getters: { monto: (f) => Number(f.monto) || 0 },
    });

    const categoriasSort = useTableSort(cierreDetail?.ventas_por_categoria || [], {
        getters: { monto: (c) => Number(c.monto) || 0 },
    });

    const exportarTotales = () => {
        const rows = (diarios.rows || []).map((r) => ({
            fecha_operacional: r.fecha_operacional?.slice?.(0, 10) || r.fecha_operacional,
            cantidad_ventas: r.cantidad_ventas,
            total_ventas: r.total_bruto,
            total_neto: r.total_neto,
            total_iva: r.total_iva,
            recibido_efectivo: r.efectivo,
            recibido_tarjeta: r.tarjeta,
            recibido_transferencia: r.transferencia,
            cajas_activas: r.cajas_activas,
        }));
        downloadCSV(`totales-diarios_${fechaDesde}_${fechaHasta}.csv`, rows);
    };

    const exportarCierres = () => {
        const rows = (cierres.rows || []).map((c) => ({
            apertura_id: c.apertura_id,
            session_id: c.session_id || '',
            sucursal: c.sucursal_nombre,
            caja: c.caja_nombre,
            cajero: c.usuario_nombre,
            cashier_id: c.cashier_id,
            fecha_operacional: c.fecha_operacional?.slice?.(0, 10) || c.fecha_operacional,
            opened_at: c.opened_at_iso || c.opened_at || c.fecha_apertura,
            closed_at: c.closed_at_iso || c.closed_at || c.fecha_cierre,
            elapsed_time: c.elapsed_time || '',
            elapsed_seconds: c.elapsed_seconds ?? '',
            estado: c.estado ? 'abierta' : 'cerrada',
            monto_inicial: c.monto_inicial,
            monto_cierre: c.monto_cierre,
            efectivo_declarado: c.estado ? '' : c.monto_cierre_efectivo,
            tarjeta_declarada: c.estado ? '' : c.monto_cierre_tarjeta,
            transferencia_declarada: c.estado ? '' : c.monto_cierre_transferencia,
            cantidad_ventas: c.cantidad_ventas,
            total_ventas: c.total_ventas,
        }));
        downloadCSV(`cierres-turno_${fechaDesde}_${fechaHasta}.csv`, rows);
    };

    const exportarCierreIndividualCSV = (detail) => {
        if (!detail) return;
        const lines = [];

        // 1. General Info
        lines.push('=== INFORMACION GENERAL ===');
        lines.push(`ID Apertura;${detail.apertura_id}`);
        lines.push(`Caja;${detail.caja_nombre || ''}`);
        lines.push(`Cajero;${detail.usuario_nombre || ''}`);
        lines.push(`Dia Operacional;${detail.fecha_operacional || ''}`);
        lines.push(`Estado;${detail.estado ? 'Abierta (En Curso)' : 'Cerrada'}`);
        lines.push(`Fecha Apertura;${detail.desde || ''}`);
        lines.push(`Fecha Cierre;${detail.estado ? '—' : detail.hasta || ''}`);
        lines.push(`Duracion;${detail.elapsed_time || ''}`);
        lines.push('');

        // 2. Payments
        lines.push('=== VENTAS POR FORMA DE PAGO ===');
        lines.push('Forma de Pago;Monto');
        if (detail.ventas_por_forma_pago) {
            detail.ventas_por_forma_pago.forEach(f => {
                lines.push(`${f.forma};${f.monto}`);
            });
        }
        const egresosVueltoEfectivo = detail.resumen_cierre?.egresos_vuelto_efectivo || 0;
        const egresosVueltoTarjeta = detail.resumen_cierre?.egresos_vuelto_tarjeta || 0;
        if (egresosVueltoEfectivo > 0) {
            lines.push(`(-) Vuelto en Efectivo (vuelto entregado);-${egresosVueltoEfectivo}`);
        }
        if (egresosVueltoTarjeta > 0) {
            lines.push(`(-) Giros en Tarjeta (efectivo entregado);-${egresosVueltoTarjeta}`);
        }
        const totalVentasReales = resolveTotalVentasReales(detail);
        lines.push(`TOTAL VENTAS REALES;${totalVentasReales}`);
        const cuadre = detail.cuadre_ventas_reales || {};
        if (Math.abs(Number(cuadre.envase_neto_boleta) || 0) >= 1) {
            lines.push(`Envases en boleta (neto);${cuadre.envase_neto_boleta}`);
        }
        if (Math.abs(Number(cuadre.redondeo_boleta) || 0) >= 1) {
            lines.push(`Redondeo boleta ($10);${cuadre.redondeo_boleta}`);
        }
        if (detail.total_ventas_categorias != null) {
            lines.push(`Total categorias (detalle_venta);${detail.total_ventas_categorias}`);
        }
        lines.push('');

        // 3. Arqueo
        const res = detail.resumen_cierre || {};
        lines.push('=== ARQUEO DE EFECTIVO ===');
        lines.push(`Monto Inicial (Base);${res.monto_base_caja || 0}`);
        lines.push(`Ventas Efectivo;${res.total_efectivo_turno || 0}`);
        lines.push(`Ingresos Caja (Adicionales);${res.ingresos_caja || 0}`);
        if ((res.ingresos_pago_cuenta || 0) > 0) {
            lines.push(`  - Pagos de cuenta (efectivo);${res.ingresos_pago_cuenta}`);
        }
        if ((res.pagos_cuenta_tarjeta_externa || 0) > 0) {
            lines.push(`  - Pagos de cuenta (tarjeta externa, informativo);${res.pagos_cuenta_tarjeta_externa}`);
        }
        if ((res.ingresos_inyeccion_efectivo || 0) > 0) {
            lines.push(`  - Inyección de efectivo;${res.ingresos_inyeccion_efectivo}`);
        }
        lines.push(`Egresos Caja (Totales);-${res.egresos_caja || 0}`);
        lines.push(`  - Vuelto entregado;-${res.egresos_vuelto_efectivo || 0}`);
        lines.push(`  - Giros (vuelto tarjeta);-${res.egresos_vuelto_tarjeta || 0}`);
        if ((res.egresos_retiro_seguridad || 0) > 0) {
            lines.push(`  - Retiro por seguridad;-${res.egresos_retiro_seguridad}`);
        }
        if (Array.isArray(detail.movimientos_efectivo) && detail.movimientos_efectivo.length > 0) {
            lines.push('');
            lines.push('=== DETALLE INYECCION / RETIRO ===');
            lines.push('Hora;Tipo;Concepto;Responsable;Monto;Desglose');
            detail.movimientos_efectivo.forEach((m) => {
                const hora = formatFechaHoraChile(m.created_at);
                const sign = m.tipo === 'ingreso' ? '' : '-';
                lines.push(`${hora};${m.tipo};${m.etiqueta};${m.responsable || ''};${sign}${m.monto};${formatDesgloseDetalle(m.desglose)}`);
            });
        }
        const egresosOtros =
            (res.egresos_caja || 0) -
            (res.egresos_vuelto_efectivo || 0) -
            (res.egresos_vuelto_tarjeta || 0) -
            (res.egresos_retiro_seguridad || 0);
        lines.push(`  - Envases / Otros;-${egresosOtros}`);
        lines.push(`Efectivo Esperado en Caja;${res.total_caja_mas_base || 0}`);
        lines.push(`Efectivo Real Contado;${detail.estado ? '—' : res.efectivo_contado || 0}`);
        lines.push(`Diferencia / Descuadre;${detail.estado ? '—' : (res.efectivo_contado || 0) - (res.total_caja_mas_base || 0)}`);
        lines.push('');

        // 4. Categories
        if (detail.ventas_por_categoria && detail.ventas_por_categoria.length > 0) {
            lines.push('=== VENTAS POR CATEGORIA ===');
            lines.push('Categoria;Monto');
            detail.ventas_por_categoria.forEach(cat => {
                lines.push(`${cat.categoria};${cat.monto}`);
            });
            const cuadreCat = detail.cuadre_ventas_reales || {};
            if (Math.abs(Number(cuadreCat.envase_neto_boleta) || 0) >= 1) {
                lines.push(`Envases en boleta (neto);${cuadreCat.envase_neto_boleta}`);
            }
            if (Math.abs(Number(cuadreCat.redondeo_boleta) || 0) >= 1) {
                lines.push(`Redondeo boleta ($10);${cuadreCat.redondeo_boleta}`);
            }
            const totalCat = detail.ventas_por_categoria.reduce((s, cat) => s + (cat.monto || 0), 0);
            lines.push(`TOTAL CATEGORIAS (productos);${totalCat}`);
            lines.push(`TOTAL CAT + AJUSTES (= ventas reales);${resolveTotalVentasReales(detail)}`);
        }

        const csvContent = '\ufeff' + lines.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `detalle_cierre_${detail.apertura_id}_${detail.caja_nombre || 'caja'}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className="page reports-page">
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '400px',
                        gap: '1rem',
                    }}
                >
                    <Loader2 size={48} className="spinning" style={{ color: 'var(--primary-color)' }} />
                    <p style={{ color: 'var(--text-secondary)' }}>Cargando reportes...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page reports-page">
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '400px',
                        gap: '1rem',
                    }}
                >
                    <AlertCircle size={48} style={{ color: 'var(--danger-color)' }} />
                    <p style={{ color: 'var(--danger-color)' }}>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page reports-page">
            <div style={{ marginBottom: '1.5rem' }}>
                <h1
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        marginBottom: '0.5rem',
                    }}
                >
                    <BarChart3 size={28} />
                    Reportes
                </h1>
                <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    Totales por <strong>día operacional</strong> (turnos que cruzan medianoche se imputan
                    al día de apertura) y cierres de turno con trazabilidad horaria.
                </p>
            </div>

            <div
                className="report-card"
                style={{
                    padding: '1rem 1.25rem',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    alignItems: 'flex-end',
                }}
            >
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
                    <span style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Desde</span>
                    <input
                        type="date"
                        value={fechaDesde}
                        max={fechaHasta}
                        onChange={(e) => setFechaDesde(e.target.value)}
                        style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid var(--border-color)' }}
                    />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
                    <span style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Hasta</span>
                    <input
                        type="date"
                        value={fechaHasta}
                        min={fechaDesde}
                        onChange={(e) => setFechaHasta(e.target.value)}
                        style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid var(--border-color)' }}
                    />
                </label>
                <button
                    type="button"
                    onClick={() => fetchReports()}
                    disabled={reloading}
                    style={{
                        padding: '0.5rem 1rem',
                        background: 'var(--primary-color)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: reloading ? 'wait' : 'pointer',
                        fontWeight: 600,
                    }}
                >
                    {reloading ? 'Cargando…' : 'Aplicar'}
                </button>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                    <button
                        type="button"
                        onClick={exportarTotales}
                        disabled={!diarios.rows?.length}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            padding: '0.5rem 0.9rem',
                            background: '#fff',
                            color: 'var(--text-main)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontWeight: 600,
                        }}
                    >
                        <Download size={16} /> Totales CSV
                    </button>
                    <button
                        type="button"
                        onClick={exportarCierres}
                        disabled={!cierres.rows?.length}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            padding: '0.5rem 0.9rem',
                            background: '#fff',
                            color: 'var(--text-main)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontWeight: 600,
                        }}
                    >
                        <Download size={16} /> Cierres CSV
                    </button>
                </div>
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1.5rem',
                }}
            >
                <SummaryCard
                    icon={<DollarSign size={22} style={{ color: 'var(--primary-color)' }} />}
                    label="Total ventas"
                    value={formatCurrency(totals.totalBruto)}
                />
                <SummaryCard
                    icon={<ShoppingCart size={22} style={{ color: 'var(--secondary-color)' }} />}
                    label="N° Ventas"
                    value={totals.totalVentas.toLocaleString('es-CL')}
                />
                <SummaryCard
                    icon={<Calendar size={22} style={{ color: '#0ea5e9' }} />}
                    label="Días con ventas"
                    value={totals.dias}
                />
                <SummaryCard
                    icon={<TrendingUp size={22} style={{ color: 'var(--accent-color)' }} />}
                    label="Promedio diario"
                    value={formatCurrency(totals.promedioDiario)}
                />
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1.5rem',
                }}
            >
                <SummaryCard
                    icon={<DollarSign size={20} style={{ color: '#16a34a' }} />}
                    label="Recibido efectivo"
                    value={formatCurrency(totals.totalEfectivo)}
                    small
                />
                <SummaryCard
                    icon={<DollarSign size={20} style={{ color: '#9333ea' }} />}
                    label="Recibido tarjeta"
                    value={formatCurrency(totals.totalTarjeta)}
                    small
                />
                <SummaryCard
                    icon={<DollarSign size={20} style={{ color: '#0ea5e9' }} />}
                    label="Recibido transferencia"
                    value={formatCurrency(totals.totalTransfer)}
                    small
                />
            </div>

            {cierresWarnings.length > 0 && (
                <div
                    className="report-card"
                    style={{
                        padding: '0.85rem 1rem',
                        marginBottom: '1.5rem',
                        background: '#fef3c7',
                        border: '1px solid #fcd34d',
                        color: '#92400e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.6rem',
                    }}
                >
                    <AlertTriangle size={18} />
                    <span>
                        <strong>{cierresWarnings.length}</strong> cierre(s) con sesión mayor a 24 h en el rango.
                        Revise los turnos resaltados (probable cierre fuera de horario).
                    </span>
                </div>
            )}

            <div className="report-card" style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem' }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.25rem' }}>
                    <Calendar size={20} /> Totales por día operacional
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                    Cada fila representa un día de negocio. Las ventas hechas tras medianoche pero antes
                    del corte (04:00) se asignan al día de apertura. «Recibido» es lo que entregó el
                    cliente (antes de vuelto / con eventual giro); el total vendido es «Total ventas».
                </p>
                {diarios.rows?.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '1.5rem' }}>
                        No hay ventas en el rango seleccionado.
                    </p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                                    <SortableTh columnKey="fecha_operacional" {...diariosSort.thProps} style={{ padding: '0.5rem' }}>Día operacional</SortableTh>
                                    <SortableTh columnKey="cantidad_ventas" {...diariosSort.thProps} style={{ padding: '0.5rem', textAlign: 'right' }} align="right">Ventas</SortableTh>
                                    <SortableTh columnKey="efectivo" {...diariosSort.thProps} style={{ padding: '0.5rem', textAlign: 'right' }} align="right" title="Monto recibido en efectivo (antes de vuelto)">Recibido efectivo</SortableTh>
                                    <SortableTh columnKey="tarjeta" {...diariosSort.thProps} style={{ padding: '0.5rem', textAlign: 'right' }} align="right" title="Monto recibido en tarjeta (débito + crédito, puede incluir giro)">Recibido tarjeta</SortableTh>
                                    <SortableTh columnKey="transferencia" {...diariosSort.thProps} style={{ padding: '0.5rem', textAlign: 'right' }} align="right" title="Monto recibido por transferencia">Recibido transfer.</SortableTh>
                                    <SortableTh columnKey="total_bruto" {...diariosSort.thProps} style={{ padding: '0.5rem', textAlign: 'right' }} align="right" title="Suma de sub_total de las ventas del día">Total ventas</SortableTh>
                                    <SortableTh columnKey="cajas_activas" {...diariosSort.thProps} style={{ padding: '0.5rem', textAlign: 'right' }} align="right">Cajas</SortableTh>
                                </tr>
                            </thead>
                            <tbody>
                                {diariosSort.sorted.map((row) => (
                                    <tr
                                        key={row.fecha_operacional}
                                        style={{ borderBottom: '1px solid var(--border-color)' }}
                                    >
                                        <td style={{ padding: '0.5rem', fontWeight: 600 }}>
                                            {formatDateLong(row.fecha_operacional)}
                                        </td>
                                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{row.cantidad_ventas}</td>
                                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                            {formatCurrency(row.efectivo)}
                                        </td>
                                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                            {formatCurrency(row.tarjeta)}
                                        </td>
                                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                            {formatCurrency(row.transferencia)}
                                        </td>
                                        <td
                                            style={{
                                                padding: '0.5rem',
                                                textAlign: 'right',
                                                fontWeight: 700,
                                                color: 'var(--secondary-color)',
                                            }}
                                        >
                                            {formatCurrency(row.total_bruto)}
                                        </td>
                                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{row.cajas_activas}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ borderTop: '2px solid var(--border-color)', fontWeight: 700 }}>
                                    <td style={{ padding: '0.5rem' }}>TOTAL</td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{totals.totalVentas}</td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                        {formatCurrency(totals.totalEfectivo)}
                                    </td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                        {formatCurrency(totals.totalTarjeta)}
                                    </td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                        {formatCurrency(totals.totalTransfer)}
                                    </td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                        {formatCurrency(totals.totalBruto)}
                                    </td>
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>

            <div className="report-card" style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem' }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.25rem' }}>
                    <Clock size={20} /> Cierres de turno
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                    Lista de aperturas y cierres con su día operacional (no la fecha calendario de la
                    madrugada). «Efectivo declarado» es el arqueo al cerrar (no lo vendido en efectivo);
                    en turnos abiertos aparece como —. Sesiones de más de 24 h se marcan en amarillo.
                </p>
                {cierres.rows?.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '1.5rem' }}>
                        No hay cierres de turno en este rango.
                    </p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="cierres-table">
                            <thead>
                                <tr>
                                    <SortableTh columnKey="fecha_operacional" {...cierresSort.thProps}>Día oper.</SortableTh>
                                    <SortableTh columnKey="sesion" {...cierresSort.thProps}>Sesión</SortableTh>
                                    <SortableTh columnKey="caja" {...cierresSort.thProps}>Caja / Cajero</SortableTh>
                                    <SortableTh columnKey="apertura" {...cierresSort.thProps}>Apertura (ISO 8601)</SortableTh>
                                    <SortableTh columnKey="cierre" {...cierresSort.thProps}>Cierre (ISO 8601)</SortableTh>
                                    <SortableTh columnKey="tiempo" {...cierresSort.thProps}>Tiempo</SortableTh>
                                    <SortableTh columnKey="cantidad_ventas" {...cierresSort.thProps} className="num" align="right">N° vtas</SortableTh>
                                    <SortableTh columnKey="monto_cierre_efectivo" {...cierresSort.thProps} className="num" align="right" title="Efectivo físico declarado en el arqueo de cierre">Efectivo declarado</SortableTh>
                                    <SortableTh columnKey="total_ventas" {...cierresSort.thProps} className="num" align="right" title="Suma de sub_total de ventas de esta sesión">Total ventas</SortableTh>
                                    <SortableTh columnKey="estado" {...cierresSort.thProps}>Estado</SortableTh>
                                    <th style={{ textAlign: 'center' }}>Detalle</th>
                                    {isAdmin && <th style={{ textAlign: 'center' }}>Acción</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {cierresSort.sorted.map((c) => {
                                    const openedISO = c.opened_at_iso || c.opened_at || c.fecha_apertura;
                                    const closedISO = c.closed_at_iso || (!c.estado ? c.closed_at || c.fecha_cierre : null);
                                    const isLong = c.elapsed_seconds && c.elapsed_seconds > 24 * 3600;
                                    return (
                                        <tr
                                            key={c.apertura_id}
                                            style={{
                                                background: isLong ? '#fffbeb' : 'transparent',
                                            }}
                                        >
                                            <td className="strong">
                                                {formatDateShort(c.fecha_operacional)}
                                            </td>
                                            <td>
                                                <span className="session-id" title={c.session_id || `apertura #${c.apertura_id}`}>
                                                    {c.session_id ? c.session_id.slice(0, 8) + '…' : `#${c.apertura_id}`}
                                                </span>
                                                {c.cashier_id && (
                                                    <span className="muted-block">cashier_id: {c.cashier_id}</span>
                                                )}
                                            </td>
                                            <td>
                                                {c.caja_nombre}
                                                {c.usuario_nombre && (
                                                    <span className="muted-block">{c.usuario_nombre}</span>
                                                )}
                                                {c.sucursal_nombre && (
                                                    <span className="muted-block">{c.sucursal_nombre}</span>
                                                )}
                                            </td>
                                            <td className="mono">{renderISO(openedISO)}</td>
                                            <td className="mono">
                                                {c.estado ? (
                                                    <span style={{ color: '#16a34a', fontStyle: 'italic' }}>
                                                        en curso
                                                    </span>
                                                ) : (
                                                    renderISO(closedISO)
                                                )}
                                            </td>
                                            <td>
                                                {c.elapsed_time || '—'}
                                                {isLong && (
                                                    <span className="warn-block">
                                                        ⚠ &gt; 24 h
                                                    </span>
                                                )}
                                            </td>
                                            <td className="num">{c.cantidad_ventas}</td>
                                            <td className="num">
                                                {c.estado
                                                    ? '—'
                                                    : formatCurrency(c.monto_cierre_efectivo || 0)}
                                            </td>
                                            <td className="num strong">
                                                {formatCurrency(c.total_ventas || 0)}
                                            </td>
                                            <td>
                                                <span
                                                    className={`status-pill ${c.estado ? 'open' : 'closed'}`}
                                                >
                                                    {c.estado ? 'Abierta' : 'Cerrada'}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleVerDetalle(c.apertura_id)}
                                                    className="btn-ver-detalle"
                                                    title="Ver Detalle"
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        color: 'var(--primary-color)',
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        padding: '0.25rem',
                                                        borderRadius: '4px',
                                                        transition: 'background 0.2s',
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                >
                                                    <Eye size={18} />
                                                </button>
                                            </td>
                                            {isAdmin && (
                                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    {c.estado ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => openCloseTurnoModal(c)}
                                                            title="Cerrar turno del cajero"
                                                            style={{
                                                                background: '#fee2e2',
                                                                border: '1px solid #fecaca',
                                                                color: '#b91c1c',
                                                                cursor: 'pointer',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '0.25rem',
                                                                padding: '0.35rem 0.55rem',
                                                                borderRadius: '6px',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            <Lock size={14} /> Cerrar
                                                        </button>
                                                    ) : (
                                                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>—</span>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <style>{`
                            .cierres-table { width: 100%; border-collapse: collapse; font-size: 0.82rem; }
                            .cierres-table thead tr { border-bottom: 2px solid var(--border-color); text-align: left; }
                            .cierres-table th, .cierres-table td { padding: 0.55rem 0.6rem; vertical-align: top; }
                            .cierres-table tbody tr { border-bottom: 1px solid var(--border-color); }
                            .cierres-table .num { text-align: right; }
                            .cierres-table .strong { font-weight: 600; }
                            .cierres-table .mono {
                                font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
                                font-size: 0.78rem;
                                white-space: nowrap;
                            }
                            .cierres-table .muted-block {
                                display: block;
                                font-size: 0.7rem;
                                color: var(--text-secondary);
                            }
                            .cierres-table .warn-block {
                                display: block;
                                font-size: 0.7rem;
                                color: #b45309;
                                font-weight: 600;
                            }
                            .cierres-table .session-id {
                                font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
                                font-size: 0.72rem;
                                color: var(--text-main);
                            }
                            .status-pill {
                                padding: 0.15rem 0.55rem;
                                border-radius: 9999px;
                                font-size: 0.72rem;
                                font-weight: 600;
                            }
                            .status-pill.open { background: rgba(16,185,129,0.12); color: #047857; }
                            .status-pill.closed { background: rgba(107,114,128,0.12); color: #374151; }
                        `}</style>
                    </div>
                )}
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
                    gap: '1.25rem',
                }}
            >
                <div className="report-card">
                    <h2
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '1.05rem',
                            marginBottom: '1rem',
                        }}
                    >
                        <Trophy size={20} style={{ color: 'var(--accent-color)' }} />
                        Top 10 productos
                    </h2>
                    {topProducts.length === 0 ? (
                        <p
                            style={{
                                color: 'var(--text-secondary)',
                                textAlign: 'center',
                                padding: '1.5rem',
                            }}
                        >
                            No hay datos de productos.
                        </p>
                    ) : (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <SortableTh columnKey="nombre" {...topProductsSort.thProps} style={{ textAlign: 'left' }}>Producto</SortableTh>
                                        <SortableTh columnKey="quantity_sold" {...topProductsSort.thProps} style={{ textAlign: 'center' }} align="center">Cant.</SortableTh>
                                        <SortableTh columnKey="revenue" {...topProductsSort.thProps} style={{ textAlign: 'right' }} align="right">Ingresos</SortableTh>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topProductsSort.sorted.map((p, i) => (
                                        <tr key={i}>
                                            <td style={{ textAlign: 'left' }}>{p.nombre}</td>
                                            <td style={{ textAlign: 'center' }}>{p.quantity_sold}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                                {formatCurrency(parseInt(p.revenue))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="report-card">
                    <h2
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '1.05rem',
                            marginBottom: '1rem',
                        }}
                    >
                        <Users size={20} style={{ color: 'var(--primary-color)' }} />
                        Ranking pre-vendedores (30 días)
                    </h2>
                    {preventasByVendedor.length === 0 ? (
                        <p
                            style={{
                                color: 'var(--text-secondary)',
                                textAlign: 'center',
                                padding: '1.5rem',
                            }}
                        >
                            No hay preventas en este período.
                        </p>
                    ) : (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <SortableTh columnKey="name" {...vendedoresSort.thProps} style={{ textAlign: 'left' }}>Vendedor</SortableTh>
                                        <SortableTh columnKey="total_preventas" {...vendedoresSort.thProps} style={{ textAlign: 'center' }} align="center">Preventas</SortableTh>
                                        <SortableTh columnKey="total_monto" {...vendedoresSort.thProps} style={{ textAlign: 'right' }} align="right">Monto</SortableTh>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vendedoresSort.sorted.map((v) => (
                                        <tr key={v.id}>
                                            <td style={{ textAlign: 'left' }}>{v.name}</td>
                                            <td style={{ textAlign: 'center' }}>{v.total_preventas}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                                {formatCurrency(parseInt(v.total_monto) || 0)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Detalle de Cierre */}
            {(selectedCierreId !== null || loadingDetail || errorDetail || cierreDetail) && (
                <div className="modal-overlay" onClick={() => { setSelectedCierreId(null); setCierreDetail(null); setErrorDetail(null); }}>
                    <div className="modal-content" style={{ maxWidth: '650px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="modal-close"
                            onClick={() => { setSelectedCierreId(null); setCierreDetail(null); setErrorDetail(null); }}
                            style={{ zIndex: 10 }}
                        >
                            &times;
                        </button>
                        
                        <h2 className="modal-title" style={{ marginBottom: '1.25rem' }}>
                            Detalle de Cierre de Caja
                        </h2>

                        {loadingDetail && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '1rem' }}>
                                <Loader2 size={36} className="spinning" style={{ color: 'var(--primary-color)' }} />
                                <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Cargando detalle...</p>
                            </div>
                        )}

                        {errorDetail && (
                            <div className="modal-alert-error" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '1rem 0' }}>
                                <AlertCircle size={18} />
                                <span>{errorDetail}</span>
                            </div>
                        )}

                        {cierreDetail && (
                            <div>
                                {/* Info General */}
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                                    gap: '0.75rem', 
                                    background: 'var(--bg-body-alt, #f9f9f9)', 
                                    padding: '1rem', 
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-color)',
                                    marginBottom: '1.25rem',
                                    fontSize: '0.85rem'
                                }}>
                                    <div>
                                        <span style={{ color: 'var(--text-secondary)', display: 'block' }}>Caja / Sucursal</span>
                                        <strong>{cierreDetail.caja_nombre || '—'}</strong>
                                    </div>
                                    <div>
                                        <span style={{ color: 'var(--text-secondary)', display: 'block' }}>Cajero / Usuario</span>
                                        <strong>{cierreDetail.usuario_nombre || '—'}</strong>
                                    </div>
                                    <div>
                                        <span style={{ color: 'var(--text-secondary)', display: 'block' }}>Día Operacional</span>
                                        <strong>{formatDateLong(cierreDetail.fecha_operacional)}</strong>
                                    </div>
                                    <div>
                                        <span style={{ color: 'var(--text-secondary)', display: 'block' }}>Estado</span>
                                        <span className={`status-pill ${cierreDetail.estado ? 'open' : 'closed'}`} style={{ display: 'inline-block', marginTop: '2px' }}>
                                            {cierreDetail.estado ? 'Abierta (En Curso)' : 'Cerrada'}
                                        </span>
                                    </div>
                                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border-color)', paddingTop: '0.5rem', marginTop: '0.25rem', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        <span>Apertura: <strong>{formatTime(cierreDetail.desde)}</strong></span>
                                        {!cierreDetail.estado && <span>Cierre: <strong>{formatTime(cierreDetail.hasta)}</strong></span>}
                                        <span>Duración: <strong>{cierreDetail.elapsed_time || '—'}</strong></span>
                                    </div>
                                </div>

                                {/* Desglose de Ventas */}
                                <h3 style={{ fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.4rem', margin: '1.5rem 0 0.75rem' }}>
                                    Ventas por Forma de Pago
                                </h3>
                                <table className="report-table" style={{ fontSize: '0.875rem', marginBottom: '1.25rem' }}>
                                    <thead>
                                        <tr>
                                            <SortableTh columnKey="forma" {...pagosSort.thProps} style={{ textAlign: 'left', padding: '0.6rem' }}>Forma de Pago</SortableTh>
                                            <SortableTh columnKey="monto" {...pagosSort.thProps} style={{ textAlign: 'right', padding: '0.6rem' }} align="right">Monto</SortableTh>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cierreDetail.ventas_por_forma_pago?.length === 0 ? (
                                            <tr>
                                                <td colSpan="2" style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '1rem' }}>No hay ventas registradas</td>
                                            </tr>
                                        ) : (
                                            pagosSort.sorted.map((f, i) => (
                                                <tr key={i}>
                                                    <td style={{ padding: '0.6rem' }}>{f.forma}</td>
                                                    <td style={{ textAlign: 'right', padding: '0.6rem', fontWeight: 600 }}>{formatCurrency(f.monto)}</td>
                                                </tr>
                                            ))
                                        )}
                                        {(cierreDetail.resumen_cierre?.egresos_vuelto_efectivo || 0) > 0 && (
                                            <tr style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                                <td style={{ padding: '0.6rem', paddingLeft: '1.25rem' }}>(-) Vuelto en Efectivo (vuelto entregado)</td>
                                                <td style={{ textAlign: 'right', padding: '0.6rem', fontWeight: 600 }}>-{formatCurrency(cierreDetail.resumen_cierre.egresos_vuelto_efectivo)}</td>
                                            </tr>
                                        )}
                                        {(cierreDetail.resumen_cierre?.egresos_vuelto_tarjeta || 0) > 0 && (
                                            <tr style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                                <td style={{ padding: '0.6rem', paddingLeft: '1.25rem' }}>(-) Giros en Tarjeta (efectivo entregado)</td>
                                                <td style={{ textAlign: 'right', padding: '0.6rem', fontWeight: 600 }}>-{formatCurrency(cierreDetail.resumen_cierre.egresos_vuelto_tarjeta)}</td>
                                            </tr>
                                        )}
                                        <tr style={{ borderTop: '2px solid var(--border-color)', fontWeight: 700 }}>
                                            <td style={{ padding: '0.6rem' }}>TOTAL VENTAS REALES ({cierreDetail.cantidad_ventas || 0} vtas)</td>
                                            <td style={{ textAlign: 'right', padding: '0.6rem', color: 'var(--secondary-color)' }}>{formatCurrency(resolveTotalVentasReales(cierreDetail))}</td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* Arqueo de Efectivo */}
                                <h3 style={{ fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.4rem', margin: '1.5rem 0 0.75rem' }}>
                                    Arqueo de Efectivo
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem', marginBottom: '1.5rem', padding: '0.5rem 0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Monto Inicial (Base de Caja):</span>
                                        <span style={{ fontWeight: 600 }}>{formatCurrency(cierreDetail.resumen_cierre?.monto_base_caja)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>(+) Ventas en Efectivo:</span>
                                        <span style={{ fontWeight: 600 }}>{formatCurrency(cierreDetail.resumen_cierre?.total_efectivo_turno)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>(+) Ajustes de Caja (Ingresos Neto):</span>
                                        <span style={{ fontWeight: 600 }}>
                                            {formatCurrency((cierreDetail.resumen_cierre?.ingresos_caja || 0) - (cierreDetail.resumen_cierre?.egresos_caja || 0))}
                                        </span>
                                    </div>
                                    <div style={{ 
                                        paddingLeft: '1.25rem', 
                                        fontSize: '0.8rem', 
                                        color: 'var(--text-secondary)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.25rem',
                                        borderLeft: '2px solid var(--border-color)',
                                        margin: '0.25rem 0'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>• Ingresos adicionales (cargos, etc.):</span>
                                            <span>{formatCurrency(cierreDetail.resumen_cierre?.ingresos_caja)}</span>
                                        </div>
                                        {(cierreDetail.resumen_cierre?.ingresos_pago_cuenta || 0) > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '0.75rem', fontStyle: 'italic' }}>
                                                <span>· Pagos de cuenta (efectivo):</span>
                                                <span>{formatCurrency(cierreDetail.resumen_cierre.ingresos_pago_cuenta)}</span>
                                            </div>
                                        )}
                                        {(cierreDetail.resumen_cierre?.pagos_cuenta_tarjeta_externa || 0) > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '0.75rem', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                                                <span>· Pagos cuenta tarj. externa (informativo):</span>
                                                <span>{formatCurrency(cierreDetail.resumen_cierre.pagos_cuenta_tarjeta_externa)}</span>
                                            </div>
                                        )}
                                        {(cierreDetail.resumen_cierre?.ingresos_inyeccion_efectivo || 0) > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '0.75rem', fontStyle: 'italic' }}>
                                                <span>· Inyección de efectivo:</span>
                                                <span>{formatCurrency(cierreDetail.resumen_cierre.ingresos_inyeccion_efectivo)}</span>
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>• Egresos adicionales (total):</span>
                                            <span style={{ fontWeight: 600 }}>-{formatCurrency(cierreDetail.resumen_cierre?.egresos_caja)}</span>
                                        </div>
                                        {(cierreDetail.resumen_cierre?.egresos_vuelto_efectivo || 0) > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '0.75rem', fontStyle: 'italic' }}>
                                                <span>- Vuelto entregado:</span>
                                                <span>-{formatCurrency(cierreDetail.resumen_cierre?.egresos_vuelto_efectivo)}</span>
                                            </div>
                                        )}
                                        {(cierreDetail.resumen_cierre?.egresos_vuelto_tarjeta || 0) > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '0.75rem', fontStyle: 'italic' }}>
                                                <span>- Giros (vuelto con tarjeta):</span>
                                                <span>-{formatCurrency(cierreDetail.resumen_cierre?.egresos_vuelto_tarjeta)}</span>
                                            </div>
                                        )}
                                        {(cierreDetail.resumen_cierre?.egresos_retiro_seguridad || 0) > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '0.75rem', fontStyle: 'italic' }}>
                                                <span>- Retiro por seguridad:</span>
                                                <span>-{formatCurrency(cierreDetail.resumen_cierre.egresos_retiro_seguridad)}</span>
                                            </div>
                                        )}
                                        {Array.isArray(cierreDetail.movimientos_efectivo) && cierreDetail.movimientos_efectivo.length > 0 && (
                                            <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px dashed var(--border-color)' }}>
                                                <div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>Detalle inyección y retiro</div>
                                                {cierreDetail.movimientos_efectivo.map((m) => {
                                                    const esIngreso = m.tipo === 'ingreso';
                                                    const lineas = desgloseLineas(m.desglose);
                                                    const hora = formatFechaHoraChile(m.created_at);
                                                    return (
                                                        <div key={m.id} style={{ paddingLeft: '0.75rem', marginBottom: '0.65rem' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
                                                                <span>
                                                                    {hora ? `${hora} · ` : ''}{esIngreso ? 'Ingreso' : 'Egreso'} · {m.etiqueta}
                                                                    {m.responsable ? ` · Responsable: ${m.responsable}` : ''}
                                                                </span>
                                                                <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                    {esIngreso ? '+' : '-'}{formatCurrency(m.monto)}
                                                                </span>
                                                            </div>
                                                            {lineas.length > 0 && (
                                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                                                                    {lineas.map((l) => `${l.qty} × ${l.label} = ${formatCurrency(l.subtotal)}`).join(' · ')}
                                                                </div>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => printComprobanteMovimientoEfectivo({
                                                                    ...m,
                                                                    caja_nombre: cierreDetail.caja_nombre,
                                                                    responsable: m.responsable || cierreDetail.usuario_nombre,
                                                                })}
                                                                style={{
                                                                    marginTop: 6,
                                                                    fontSize: 12,
                                                                    fontWeight: 600,
                                                                    border: '1px solid var(--border-color)',
                                                                    background: '#fff',
                                                                    borderRadius: 8,
                                                                    padding: '6px 10px',
                                                                    cursor: 'pointer',
                                                                    minHeight: 36,
                                                                }}
                                                            >
                                                                Imprimir comprobante
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {((cierreDetail.resumen_cierre?.egresos_caja || 0) - (cierreDetail.resumen_cierre?.egresos_vuelto_efectivo || 0) - (cierreDetail.resumen_cierre?.egresos_vuelto_tarjeta || 0) - (cierreDetail.resumen_cierre?.egresos_retiro_seguridad || 0)) > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '0.75rem', fontStyle: 'italic' }}>
                                                <span>- Devolución de envases / otros:</span>
                                                <span>-{formatCurrency((cierreDetail.resumen_cierre?.egresos_caja || 0) - (cierreDetail.resumen_cierre?.egresos_vuelto_efectivo || 0) - (cierreDetail.resumen_cierre?.egresos_vuelto_tarjeta || 0) - (cierreDetail.resumen_cierre?.egresos_retiro_seguridad || 0))}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed var(--border-color)', paddingTop: '0.5rem', fontWeight: 600 }}>
                                        <span>(=) Efectivo Esperado en Caja:</span>
                                        <span>{formatCurrency(cierreDetail.resumen_cierre?.total_caja_mas_base)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: cierreDetail.estado ? 'var(--text-secondary)' : 'inherit' }}>
                                        <span>( ) Efectivo Real Contado (Físico):</span>
                                        <span style={{ fontWeight: 700 }}>
                                            {cierreDetail.estado 
                                                ? '— (Caja abierta)' 
                                                : formatCurrency(cierreDetail.resumen_cierre?.efectivo_contado || 0)}
                                        </span>
                                    </div>
                                    
                                    {!cierreDetail.estado && (
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            borderTop: '2px solid var(--border-color)', 
                                            paddingTop: '0.5rem', 
                                            fontWeight: 700,
                                            fontSize: '0.95rem',
                                            color: (cierreDetail.resumen_cierre?.efectivo_contado || 0) - (cierreDetail.resumen_cierre?.total_caja_mas_base || 0) < 0 
                                                ? 'var(--danger-color, #ef4444)' 
                                                : (cierreDetail.resumen_cierre?.efectivo_contado || 0) - (cierreDetail.resumen_cierre?.total_caja_mas_base || 0) > 0 
                                                    ? '#10b981' 
                                                    : 'inherit'
                                        }}>
                                            <span>Diferencia / Descuadre:</span>
                                            <span>
                                                {formatCurrency((cierreDetail.resumen_cierre?.efectivo_contado || 0) - (cierreDetail.resumen_cierre?.total_caja_mas_base || 0))}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Categorías vendidas */}
                                {cierreDetail.ventas_por_categoria?.length > 0 && (
                                    <>
                                        <h3 style={{ fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.4rem', margin: '1.5rem 0 0.75rem' }}>
                                            Ventas por Categoría de Producto
                                        </h3>
                                        <div style={{ maxHeight: '180px', overflowY: 'auto', marginBottom: '1.5rem' }}>
                                            <table className="report-table" style={{ fontSize: '0.82rem' }}>
                                                <thead>
                                                    <tr>
                                                        <SortableTh columnKey="categoria" {...categoriasSort.thProps} style={{ textAlign: 'left', padding: '0.5rem' }}>Categoría</SortableTh>
                                                        <SortableTh columnKey="monto" {...categoriasSort.thProps} style={{ textAlign: 'right', padding: '0.5rem' }} align="right">Monto</SortableTh>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {categoriasSort.sorted.map((cat, idx) => (
                                                        <tr key={idx}>
                                                            <td style={{ padding: '0.5rem' }}>{cat.categoria}</td>
                                                            <td style={{ textAlign: 'right', padding: '0.5rem', fontWeight: 600 }}>{formatCurrency(cat.monto)}</td>
                                                        </tr>
                                                    ))}
                                                    {Math.abs(Number(cierreDetail.cuadre_ventas_reales?.envase_neto_boleta) || 0) >= 1 && (
                                                        <tr style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                                            <td style={{ padding: '0.5rem', paddingLeft: '1.25rem' }}>Envases en boleta (neto)</td>
                                                            <td style={{ textAlign: 'right', padding: '0.5rem', fontWeight: 600 }}>{formatCurrency(cierreDetail.cuadre_ventas_reales.envase_neto_boleta)}</td>
                                                        </tr>
                                                    )}
                                                    {Math.abs(Number(cierreDetail.cuadre_ventas_reales?.redondeo_boleta) || 0) >= 1 && (
                                                        <tr style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                                            <td style={{ padding: '0.5rem', paddingLeft: '1.25rem' }}>Redondeo boleta ($10)</td>
                                                            <td style={{ textAlign: 'right', padding: '0.5rem', fontWeight: 600 }}>{formatCurrency(cierreDetail.cuadre_ventas_reales.redondeo_boleta)}</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                                <tfoot>
                                                    <tr style={{ borderTop: '2px solid var(--border-color)', fontWeight: 700 }}>
                                                        <td style={{ padding: '0.5rem' }}>TOTAL (= ventas reales)</td>
                                                        <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                                                            {formatCurrency(resolveTotalVentasReales(cierreDetail))}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        <div className="modal-actions" style={{ justifyContent: 'flex-end', marginTop: '1.5rem', gap: '0.75rem' }}>
                            {cierreDetail && (
                                <>
                                    {isAdmin && cierreDetail.estado && (
                                        <button
                                            type="button"
                                            className="modal-btn-primary"
                                            onClick={() => openCloseTurnoModal({
                                                apertura_id: cierreDetail.apertura_id,
                                                caja_nombre: cierreDetail.caja_nombre,
                                                usuario_nombre: cierreDetail.usuario_nombre,
                                            })}
                                            style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.75rem 1rem', background: '#b91c1c', color: '#fff' }}
                                        >
                                            <Lock size={16} /> Cerrar turno
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="modal-btn-primary"
                                        onClick={() => window.print()}
                                        style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.75rem 1rem', background: '#0284c7', color: '#fff', boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)' }}
                                    >
                                        <Printer size={16} /> Imprimir PDF
                                    </button>
                                    <button
                                        type="button"
                                        className="modal-btn-primary"
                                        onClick={() => exportarCierreIndividualCSV(cierreDetail)}
                                        style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.75rem 1rem' }}
                                    >
                                        <Download size={16} /> Exportar CSV
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                className="modal-btn-secondary"
                                onClick={() => { setSelectedCierreId(null); setCierreDetail(null); setErrorDetail(null); }}
                                style={{ flex: 'none', minWidth: '100px', margin: 0 }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Admin cierra turno de cajero */}
            {closeTurnoTarget && (
                <div
                    className="modal-overlay"
                    onClick={() => {
                        if (!closeTurnoLoading) {
                            setCloseTurnoTarget(null);
                            setCloseTurnoResumen(null);
                            setCloseTurnoError(null);
                        }
                    }}
                >
                    <div
                        className="modal-content"
                        style={{ maxWidth: '460px', width: '90%', maxHeight: '92vh', overflowY: 'auto' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="modal-close"
                            disabled={closeTurnoLoading}
                            onClick={() => {
                                setCloseTurnoTarget(null);
                                setCloseTurnoResumen(null);
                                setCloseTurnoError(null);
                            }}
                        >
                            &times;
                        </button>
                        <h2 className="modal-title" style={{ marginBottom: '0.75rem' }}>
                            Cerrar turno de cajero
                        </h2>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 1rem' }}>
                            Caja: <strong>{closeTurnoTarget.caja_nombre}</strong>
                            <br />
                            Cajero: <strong>{closeTurnoTarget.usuario_nombre}</strong>
                            <br />
                            Apertura #{closeTurnoTarget.apertura_id}
                        </p>
                        {closeTurnoLoading && !closeTurnoResumen ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                                <Loader2 size={28} className="spinning" style={{ color: 'var(--primary-color)' }} />
                            </div>
                        ) : (
                            <>
                                {closeTurnoResumen && (
                                    <div style={{ fontSize: '0.875rem', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>Efectivo esperado en caja</span>
                                            <strong>{formatCurrency(closeTurnoResumen.efectivo_esperado_en_caja)}</strong>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)' }}>
                                            <span>Monto inicial</span>
                                            <span>{formatCurrency(closeTurnoResumen.monto_inicial)}</span>
                                        </div>
                                    </div>
                                )}
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.35rem' }}>
                                    Efectivo contado
                                </label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={closeTurnoEfectivo}
                                    onChange={(e) => setCloseTurnoEfectivo(e.target.value)}
                                    disabled={closeTurnoLoading}
                                    placeholder="Ej: 110000"
                                    style={{
                                        width: '100%',
                                        padding: '0.65rem 0.75rem',
                                        borderRadius: '8px',
                                        border: '1px solid var(--border-color)',
                                        marginBottom: '0.75rem',
                                        fontSize: '1rem',
                                    }}
                                />
                                <div style={{ marginBottom: '0.75rem' }}>
                                    <DenominacionEfectivo
                                        value={closeTurnoDesglose}
                                        onChange={(next, total) => {
                                            setCloseTurnoDesglose(next);
                                            setCloseTurnoEfectivo(String(total));
                                        }}
                                        label="Cantidad de billetes y monedas"
                                        accentColor="#b91c1c"
                                        disabled={closeTurnoLoading}
                                        compact
                                        showTotal={false}
                                    />
                                </div>
                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        fontSize: '0.8rem',
                                        marginBottom: '0.75rem',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={closeTurnoForzar}
                                        onChange={(e) => setCloseTurnoForzar(e.target.checked)}
                                        disabled={closeTurnoLoading}
                                    />
                                    Cerrar con descuadre (si el contado no cuadra)
                                </label>
                                {closeTurnoError && (
                                    <div
                                        style={{
                                            background: '#fef2f2',
                                            border: '1px solid #fecaca',
                                            color: '#991b1b',
                                            borderRadius: '8px',
                                            padding: '0.65rem 0.75rem',
                                            fontSize: '0.8rem',
                                            marginBottom: '0.75rem',
                                            whiteSpace: 'pre-line',
                                        }}
                                    >
                                        {closeTurnoError}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        className="modal-btn-secondary"
                                        disabled={closeTurnoLoading}
                                        onClick={() => {
                                            setCloseTurnoTarget(null);
                                            setCloseTurnoResumen(null);
                                            setCloseTurnoError(null);
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="modal-btn-primary"
                                        disabled={closeTurnoLoading}
                                        onClick={submitCloseTurno}
                                        style={{ background: '#b91c1c', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                                    >
                                        {closeTurnoLoading ? <Loader2 size={16} className="spinning" /> : <Lock size={16} />}
                                        Confirmar cierre
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Area de Impresión PDF */}
            {cierreDetail && (
                <div className="print-area">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '3px solid #000', paddingBottom: '0.75rem', marginBottom: '1.5rem' }}>
                        <div>
                            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>SUPER COLCHAGUA</h1>
                            <p style={{ fontSize: '0.8rem', color: '#555', margin: '2px 0 0' }}>Minimarket &amp; Punto de Venta</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>COMPROBANTE DE CIERRE</h2>
                            <p style={{ fontSize: '0.8rem', color: '#555', margin: '2px 0 0' }}>ID Apertura: #{cierreDetail.apertura_id}</p>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem', fontSize: '0.9rem', border: '1px solid #ddd', padding: '1rem', borderRadius: '4px' }}>
                        <div>
                            <p style={{ margin: '0 0 0.5rem' }}><strong>Caja:</strong> {cierreDetail.caja_nombre || '—'}</p>
                            <p style={{ margin: '0 0 0.5rem' }}><strong>Cajero:</strong> {cierreDetail.usuario_nombre || '—'}</p>
                            <p style={{ margin: 0 }}><strong>Día Operacional:</strong> {formatDateLong(cierreDetail.fecha_operacional)}</p>
                        </div>
                        <div>
                            <p style={{ margin: '0 0 0.5rem' }}><strong>Fecha Apertura:</strong> {formatTime(cierreDetail.desde)}</p>
                            <p style={{ margin: '0 0 0.5rem' }}><strong>Fecha Cierre:</strong> {cierreDetail.estado ? 'En curso' : formatTime(cierreDetail.hasta)}</p>
                            <p style={{ margin: 0 }}><strong>Duración:</strong> {cierreDetail.elapsed_time || '—'}</p>
                        </div>
                    </div>

                    <h3 style={{ fontSize: '1.1rem', borderBottom: '2px solid #000', paddingBottom: '0.3rem', margin: '1.5rem 0 0.75rem' }}>
                        Ventas por Forma de Pago
                    </h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                        <thead>
                            <tr style={{ borderBottom: '1.5px solid #000', textAlign: 'left' }}>
                                <th style={{ padding: '0.5rem 0' }}>Forma de Pago</th>
                                <th style={{ padding: '0.5rem 0', textAlign: 'right' }}>Monto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cierreDetail.ventas_por_forma_pago?.map((f, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '0.5rem 0' }}>{f.forma}</td>
                                    <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(f.monto)}</td>
                                </tr>
                            ))}
                            {(cierreDetail.resumen_cierre?.egresos_vuelto_efectivo || 0) > 0 && (
                                <tr style={{ borderBottom: '1px solid #eee', color: '#555', fontStyle: 'italic' }}>
                                    <td style={{ padding: '0.5rem 0 0.5rem 1rem' }}>(-) Vuelto en Efectivo (vuelto entregado)</td>
                                    <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600 }}>-{formatCurrency(cierreDetail.resumen_cierre.egresos_vuelto_efectivo)}</td>
                                </tr>
                            )}
                            {(cierreDetail.resumen_cierre?.egresos_vuelto_tarjeta || 0) > 0 && (
                                <tr style={{ borderBottom: '1px solid #eee', color: '#555', fontStyle: 'italic' }}>
                                    <td style={{ padding: '0.5rem 0 0.5rem 1rem' }}>(-) Giros en Tarjeta (efectivo entregado)</td>
                                    <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600 }}>-{formatCurrency(cierreDetail.resumen_cierre.egresos_vuelto_tarjeta)}</td>
                                </tr>
                            )}
                            <tr style={{ borderTop: '1.5px solid #000', fontWeight: 700 }}>
                                <td style={{ padding: '0.75rem 0' }}>TOTAL VENTAS REALES ({cierreDetail.cantidad_ventas || 0} vtas)</td>
                                <td style={{ padding: '0.75rem 0', textAlign: 'right' }}>{formatCurrency(resolveTotalVentasReales(cierreDetail))}</td>
                            </tr>
                        </tbody>
                    </table>

                    <h3 style={{ fontSize: '1.1rem', borderBottom: '2px solid #000', paddingBottom: '0.3rem', margin: '1.5rem 0 0.75rem' }}>
                        Arqueo y Cuadre de Caja
                    </h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '0.5rem 0' }}>Monto Inicial (Base de Caja)</td>
                                <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(cierreDetail.resumen_cierre?.monto_base_caja)}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '0.5rem 0' }}>(+) Ventas en Efectivo</td>
                                <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(cierreDetail.resumen_cierre?.total_efectivo_turno)}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '0.5rem 0' }}>(+) Ingresos adicionales (cargos envases, etc.)</td>
                                <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(cierreDetail.resumen_cierre?.ingresos_caja)}</td>
                            </tr>
                            {(cierreDetail.resumen_cierre?.ingresos_pago_cuenta || 0) > 0 && (
                                <tr style={{ borderBottom: '1px dashed #eee', fontSize: '0.8rem', color: '#555' }}>
                                    <td style={{ padding: '0.4rem 0 0.4rem 1rem' }}>· Pagos de cuenta (efectivo)</td>
                                    <td style={{ padding: '0.4rem 0', textAlign: 'right' }}>{formatCurrency(cierreDetail.resumen_cierre.ingresos_pago_cuenta)}</td>
                                </tr>
                            )}
                            {(cierreDetail.resumen_cierre?.pagos_cuenta_tarjeta_externa || 0) > 0 && (
                                <tr style={{ borderBottom: '1px dashed #eee', fontSize: '0.8rem', color: '#555' }}>
                                    <td style={{ padding: '0.4rem 0 0.4rem 1rem' }}>· Pagos cuenta tarj. externa (informativo)</td>
                                    <td style={{ padding: '0.4rem 0', textAlign: 'right' }}>{formatCurrency(cierreDetail.resumen_cierre.pagos_cuenta_tarjeta_externa)}</td>
                                </tr>
                            )}
                            {(cierreDetail.resumen_cierre?.ingresos_inyeccion_efectivo || 0) > 0 && (
                                <tr style={{ borderBottom: '1px dashed #eee', fontSize: '0.8rem', color: '#555' }}>
                                    <td style={{ padding: '0.4rem 0 0.4rem 1rem' }}>· Inyección de efectivo</td>
                                    <td style={{ padding: '0.4rem 0', textAlign: 'right' }}>{formatCurrency(cierreDetail.resumen_cierre.ingresos_inyeccion_efectivo)}</td>
                                </tr>
                            )}
                            <tr style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '0.5rem 0' }}>(-) Egresos adicionales (total)</td>
                                <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600 }}>-{formatCurrency(cierreDetail.resumen_cierre?.egresos_caja)}</td>
                            </tr>
                            {(cierreDetail.resumen_cierre?.egresos_vuelto_efectivo || 0) > 0 && (
                                <tr style={{ borderBottom: '1px dashed #eee', fontSize: '0.8rem', color: '#555' }}>
                                    <td style={{ padding: '0.4rem 0 0.4rem 1rem' }}>- Vuelto entregado (efectivo)</td>
                                    <td style={{ padding: '0.4rem 0', textAlign: 'right' }}>-{formatCurrency(cierreDetail.resumen_cierre?.egresos_vuelto_efectivo)}</td>
                                </tr>
                            )}
                            {(cierreDetail.resumen_cierre?.egresos_vuelto_tarjeta || 0) > 0 && (
                                <tr style={{ borderBottom: '1px dashed #eee', fontSize: '0.8rem', color: '#555' }}>
                                    <td style={{ padding: '0.4rem 0 0.4rem 1rem' }}>- Giros (vuelto con tarjeta)</td>
                                    <td style={{ padding: '0.4rem 0', textAlign: 'right' }}>-{formatCurrency(cierreDetail.resumen_cierre?.egresos_vuelto_tarjeta)}</td>
                                </tr>
                            )}
                            {(cierreDetail.resumen_cierre?.egresos_retiro_seguridad || 0) > 0 && (
                                <tr style={{ borderBottom: '1px dashed #eee', fontSize: '0.8rem', color: '#555' }}>
                                    <td style={{ padding: '0.4rem 0 0.4rem 1rem' }}>- Retiro por seguridad</td>
                                    <td style={{ padding: '0.4rem 0', textAlign: 'right' }}>-{formatCurrency(cierreDetail.resumen_cierre.egresos_retiro_seguridad)}</td>
                                </tr>
                            )}
                            {Array.isArray(cierreDetail.movimientos_efectivo) && cierreDetail.movimientos_efectivo.length > 0 && (
                                <>
                                    <tr>
                                        <td colSpan={2} style={{ padding: '0.6rem 0 0.3rem', fontWeight: 700, fontSize: '0.85rem' }}>Detalle inyección y retiro</td>
                                    </tr>
                                    {cierreDetail.movimientos_efectivo.map((m) => {
                                        const esIngreso = m.tipo === 'ingreso';
                                        const lineas = desgloseLineas(m.desglose);
                                        const hora = formatFechaHoraChile(m.created_at);
                                        return (
                                            <tr key={m.id} style={{ borderBottom: '1px dashed #eee', fontSize: '0.8rem', color: '#555' }}>
                                                <td style={{ padding: '0.45rem 0 0.45rem 1rem' }}>
                                                    {hora ? `${hora} · ` : ''}{esIngreso ? 'Ingreso' : 'Egreso'} · {m.etiqueta}
                                                    <div>Responsable: {m.responsable || cierreDetail.usuario_nombre || '—'}</div>
                                                    {lineas.map((l) => (
                                                        <div key={l.valor}>{l.qty} × {l.label} = {formatCurrency(l.subtotal)}</div>
                                                    ))}
                                                </td>
                                                <td style={{ padding: '0.45rem 0', textAlign: 'right', verticalAlign: 'top' }}>
                                                    {esIngreso ? '+' : '-'}{formatCurrency(m.monto)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </>
                            )}
                            {((cierreDetail.resumen_cierre?.egresos_caja || 0) - (cierreDetail.resumen_cierre?.egresos_vuelto_efectivo || 0) - (cierreDetail.resumen_cierre?.egresos_vuelto_tarjeta || 0) - (cierreDetail.resumen_cierre?.egresos_retiro_seguridad || 0)) > 0 && (
                                <tr style={{ borderBottom: '1px dashed #eee', fontSize: '0.8rem', color: '#555' }}>
                                    <td style={{ padding: '0.4rem 0 0.4rem 1rem' }}>- Devolución de envases / otros</td>
                                    <td style={{ padding: '0.4rem 0', textAlign: 'right' }}>-{formatCurrency((cierreDetail.resumen_cierre?.egresos_caja || 0) - (cierreDetail.resumen_cierre?.egresos_vuelto_efectivo || 0) - (cierreDetail.resumen_cierre?.egresos_vuelto_tarjeta || 0) - (cierreDetail.resumen_cierre?.egresos_retiro_seguridad || 0))}</td>
                                </tr>
                            )}
                            <tr style={{ borderBottom: '1.5px solid #000', fontWeight: 700 }}>
                                <td style={{ padding: '0.6rem 0' }}>(=) Efectivo Esperado en Caja</td>
                                <td style={{ padding: '0.6rem 0', textAlign: 'right' }}>{formatCurrency(cierreDetail.resumen_cierre?.total_caja_mas_base)}</td>
                            </tr>
                            <tr style={{ borderBottom: '1.5px solid #000', fontWeight: 700 }}>
                                <td style={{ padding: '0.6rem 0' }}>Efectivo Físico Contado</td>
                                <td style={{ padding: '0.6rem 0', textAlign: 'right' }}>
                                    {cierreDetail.estado ? '—' : formatCurrency(cierreDetail.resumen_cierre?.efectivo_contado || 0)}
                                </td>
                            </tr>
                            {!cierreDetail.estado && (
                                <tr style={{ 
                                    fontWeight: 800, 
                                    fontSize: '1rem',
                                    color: (cierreDetail.resumen_cierre?.efectivo_contado || 0) - (cierreDetail.resumen_cierre?.total_caja_mas_base || 0) < 0 
                                        ? '#d32f2f' 
                                        : (cierreDetail.resumen_cierre?.efectivo_contado || 0) - (cierreDetail.resumen_cierre?.total_caja_mas_base || 0) > 0 
                                            ? '#2e7d32' 
                                            : '#000'
                                }}>
                                    <td style={{ padding: '0.75rem 0' }}>Diferencia / Descuadre</td>
                                    <td style={{ padding: '0.75rem 0', textAlign: 'right' }}>
                                        {formatCurrency((cierreDetail.resumen_cierre?.efectivo_contado || 0) - (cierreDetail.resumen_cierre?.total_caja_mas_base || 0))}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {cierreDetail.ventas_por_categoria?.length > 0 && (
                        <>
                            <h3 style={{ fontSize: '1.1rem', borderBottom: '2px solid #000', paddingBottom: '0.3rem', margin: '2rem 0 0.75rem' }}>
                                Ventas por Categoría de Producto
                            </h3>
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1.5px solid #000', textAlign: 'left' }}>
                                        <th style={{ padding: '0.5rem 0' }}>Categoría</th>
                                        <th style={{ padding: '0.5rem 0', textAlign: 'right' }}>Monto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cierreDetail.ventas_por_categoria.map((cat, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '0.5rem 0' }}>{cat.categoria}</td>
                                            <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(cat.monto)}</td>
                                        </tr>
                                    ))}
                                    {Math.abs(Number(cierreDetail.cuadre_ventas_reales?.envase_neto_boleta) || 0) >= 1 && (
                                        <tr style={{ borderBottom: '1px solid #eee', color: '#555', fontStyle: 'italic' }}>
                                            <td style={{ padding: '0.5rem 0 0.5rem 1rem' }}>Envases en boleta (neto)</td>
                                            <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(cierreDetail.cuadre_ventas_reales.envase_neto_boleta)}</td>
                                        </tr>
                                    )}
                                    {Math.abs(Number(cierreDetail.cuadre_ventas_reales?.redondeo_boleta) || 0) >= 1 && (
                                        <tr style={{ borderBottom: '1px solid #eee', color: '#555', fontStyle: 'italic' }}>
                                            <td style={{ padding: '0.5rem 0 0.5rem 1rem' }}>Redondeo boleta ($10)</td>
                                            <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(cierreDetail.cuadre_ventas_reales.redondeo_boleta)}</td>
                                        </tr>
                                    )}
                                    <tr style={{ borderTop: '1.5px solid #000', fontWeight: 700 }}>
                                        <td style={{ padding: '0.6rem 0' }}>TOTAL (= ventas reales)</td>
                                        <td style={{ padding: '0.6rem 0', textAlign: 'right' }}>
                                            {formatCurrency(resolveTotalVentasReales(cierreDetail))}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', marginTop: '5rem', textAlign: 'center', fontSize: '0.9rem' }}>
                        <div>
                            <div style={{ borderTop: '1px solid #000', width: '200px', margin: '0 auto 0.5rem' }}></div>
                            <p style={{ margin: 0 }}>Firma Cajero</p>
                            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#555' }}>{cierreDetail.usuario_nombre}</p>
                        </div>
                        <div>
                            <div style={{ borderTop: '1px solid #000', width: '200px', margin: '0 auto 0.5rem' }}></div>
                            <p style={{ margin: 0 }}>Firma Supervisor</p>
                            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#555' }}>Control de Caja</p>
                        </div>
                    </div>

                    <div style={{ borderTop: '1px solid #ddd', marginTop: '3rem', paddingTop: '0.5rem', textAlign: 'center', fontSize: '0.75rem', color: '#777' }}>
                        Documento generado el {formatFechaHoraChile(new Date())} | ERP Minimarket
                    </div>
                </div>
            )}

            <style>{`
                @media screen {
                    .print-area {
                        display: none !important;
                    }
                }
                @media print {
                    /* Hide everything except the print-area container */
                    body * {
                        visibility: hidden !important;
                    }
                    .print-area, .print-area * {
                        visibility: visible !important;
                    }
                    .print-area {
                        display: block !important;
                        position: absolute !important;
                        left: 0 !important;
                        top: 0 !important;
                        width: 100% !important;
                        background: #fff !important;
                        color: #000 !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                }
            `}</style>
        </div>
    );
}

function SummaryCard({ icon, label, value, small = false }) {
    return (
        <div
            className="report-card"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.85rem',
                padding: small ? '0.75rem 1rem' : '1rem 1.25rem',
            }}
        >
            <div
                style={{
                    background: 'rgba(99,102,241,0.08)',
                    borderRadius: 10,
                    padding: '0.5rem',
                    display: 'flex',
                }}
            >
                {icon}
            </div>
            <div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', margin: 0 }}>{label}</p>
                <p
                    style={{
                        fontSize: small ? '1.05rem' : '1.3rem',
                        fontWeight: 700,
                        margin: 0,
                        color: 'var(--text-main)',
                    }}
                >
                    {value}
                </p>
            </div>
        </div>
    );
}
